import React, { useEffect, useState } from "react";

type GalleryProps = {
  photos: string[];
  altPrefix?: string;
};

// Photo grid + lightbox, extracted from the original landing page.
function Gallery({ photos, altPrefix = "Event photo" }: GalleryProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const goPrev = () =>
    setActiveIndex((i) =>
      i === null ? null : (i - 1 + photos.length) % photos.length,
    );
  const goNext = () =>
    setActiveIndex((i) => (i === null ? null : (i + 1) % photos.length));

  useEffect(() => {
    if (activeIndex === null) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActiveIndex(null);
      if (event.key === "ArrowLeft") goPrev();
      if (event.key === "ArrowRight") goNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex]);

  const activePhoto = activeIndex !== null ? photos[activeIndex] : null;

  return (
    <>
      <div className="gallery-grid">
        {photos.map((photo, index) => (
          <figure className="gallery-card" key={photo}>
            <button
              type="button"
              className="gallery-button"
              onClick={() => setActiveIndex(index)}
              aria-label={`Open ${altPrefix.toLowerCase()} ${index + 1}`}
            >
              <img
                className="gallery-photo"
                src={photo}
                alt={`${altPrefix} ${index + 1}`}
                loading="lazy"
              />
            </button>
          </figure>
        ))}
      </div>

      {activePhoto ? (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Photo lightbox"
          onClick={() => setActiveIndex(null)}
        >
          <div
            className="lightbox-content"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="lightbox-nav lightbox-nav-prev"
              onClick={goPrev}
              aria-label="Show previous photo"
            >
              &lt;
            </button>
            <button
              type="button"
              className="lightbox-nav lightbox-nav-next"
              onClick={goNext}
              aria-label="Show next photo"
            >
              &gt;
            </button>
            <button
              type="button"
              className="lightbox-close"
              onClick={() => setActiveIndex(null)}
              aria-label="Close lightbox"
            >
              x
            </button>
            <img
              className="lightbox-image"
              src={activePhoto}
              alt={`${altPrefix} ${(activeIndex ?? 0) + 1}`}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}

export default Gallery;
